import React, { useState, useEffect, useMemo } from 'react';
import type { PoFile } from 'shared/types.js';
import { api } from './api.js';

const EMPTY_ARRAY: string[] = [];

const getFileTypeBadge = (path?: string): { text: string; className: string } | null => {
  if (!path) return null;
  const decodedPath = decodeURIComponent(path);
  if (decodedPath.includes("PO's")) {
      return { text: 'PO', className: 'po-badge' };
  }
  if (decodedPath.includes('Packing Slips')) {
      return { text: 'Packing Slip', className: 'slip-badge' };
  }
  if (decodedPath.includes('HH Invoices')) {
      return { text: 'Invoice', className: 'invoice-badge' };
  }
  return null;
};

export const PoFilesDisplay = ({ poNumber, reference, invoice_no, additionalPoNumbers = EMPTY_ARRAY, forceSearch = false }: { poNumber: string; reference: string; invoice_no: string;additionalPoNumbers?: string[], forceSearch?: boolean }) => {
  const [files, setFiles] = useState<PoFile[]>([]);
  const [activeFile, setActiveFile] = useState<PoFile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  useEffect(() => {
    if (isModalOpen) {
        const count = parseInt(document.body.dataset.modalOpenCount || '0', 10);
        if (count === 0) {
            document.body.classList.add('modal-open');
        }
        document.body.dataset.modalOpenCount = String(count + 1);
        return () => {
            const count = parseInt(document.body.dataset.modalOpenCount || '0', 10);
            const newCount = Math.max(0, count - 1);
            if (newCount === 0) {
                document.body.classList.remove('modal-open');
            }
            document.body.dataset.modalOpenCount = String(newCount);
        };
    }
  }, [isModalOpen]);
  const poNumbersToSearch = useMemo(() => {
    const searchTerms = new Set<string>();
    if (forceSearch) {
        if(poNumber) searchTerms.add(poNumber);
        return Array.from(searchTerms);
    }
    const poSources = [poNumber, reference, ...additionalPoNumbers];
    for (const text of poSources) {
      if (!text) continue;
      
      const poExtractionRegex = /\b(\d{2})[- ]?(\d{6,7})\b/g;
      let match;
      while ((match = poExtractionRegex.exec(text)) !== null) {
        searchTerms.add(`${match[1]}-${match[2]}`);
      }

      const fourDigitMatches = text.match(/\b\d{4}\b/g);
      if (fourDigitMatches) {
        fourDigitMatches.forEach(match => searchTerms.add(match));
      }
    }
    if (invoice_no) {
        searchTerms.add(invoice_no);
    }
    return Array.from(searchTerms);
  }, [poNumber, reference, invoice_no, additionalPoNumbers, forceSearch]);
  useEffect(() => {
    setActiveFile(null);
    setIsModalOpen(false);
    setFiles([]);
    if (poNumbersToSearch.length === 0) {
      setIsLoading(false);
      return;
    }
    const controller = new AbortController();
    setIsLoading(true);
    const fetchAllFiles = async () => {
      try {
        const promises = poNumbersToSearch.map(async (po) => {
          try {
            const isFourDigitPo = /^\d{4}$/.test(po);
            const searchQuery = isFourDigitPo ? `${po}.pdf` : po;
            const response = await api.fetch(`/api/files/search?query=${encodeURIComponent(searchQuery)}`, { signal: controller.signal });
            let fetchedFiles = await response.json() as PoFile[];
            if (isFourDigitPo) {
                 const expectedFilename = `${po}.pdf`;
                 fetchedFiles = fetchedFiles.filter(file => file.name.toLowerCase() === expectedFilename.toLowerCase());
            }
            return fetchedFiles;
          } catch (e) {
            return [];
          }
        });
        const results = await Promise.all(promises);
        if (controller.signal.aborted) return;
        const allFiles = results.flat();
        const uniqueFiles = allFiles.filter((file, index, self) =>
            index === self.findIndex((f) => f.previewUrl === file.previewUrl)
        );
        setFiles(uniqueFiles);
      } catch (e) {
          // No-op
      } finally {
        if (!controller.signal.aborted) {
            setIsLoading(false);
        }
      }
    };
    fetchAllFiles();
    return () => controller.abort();
  }, [poNumbersToSearch]);
  const openModal = () => {
    if (files.length > 0) setIsModalOpen(true);
  };
  const closeModal = () => {
    setIsModalOpen(false);
    setActiveFile(null);
  };
  if (poNumbersToSearch.length === 0) return null;
  if (isLoading) return <span className="po-files-status">Searching for documents...</span>;
  if (files.length === 0) return <span className="po-files-status">No documents found.</span>;
  return (
    <>
      <button className="linklike" onClick={openModal}>
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className="file-icon"><path d="M4 6h16v2H4zm0 5h16v2H4zm0 5h16v2H4z"></path></svg>
        View {files.length} Document{files.length > 1 ? 's' : ''}
      </button>
      {isModalOpen && (
        <div className="modal-backdrop" onClick={closeModal} role="dialog" aria-modal="true" aria-labelledby="modal-title">
          <div className="modal modal-split-view" onClick={e => e.stopPropagation()}>
            <header className="modal-header">
              <strong id="modal-title">Related Documents ({files.length})</strong>
              <button onClick={closeModal} aria-label="Close modal">&times;</button>
            </header>
            <div className="modal-body-split">
              <aside className="modal-sidebar">
                <ul className="modal-file-list">
                  {files.map(f => {
                    const badge = getFileTypeBadge(f.path);
                    return (
                      <li key={f.previewUrl} className={activeFile?.previewUrl === f.previewUrl ? 'active' : ''}>
                        <button className="modal-file-item" onClick={() => setActiveFile(f)}>
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className="file-icon"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zM6 20V4h7v5h5v11H6z"></path></svg>
                          <div className="modal-file-item-content">
                            <span title={f.name}>{f.name}</span>
                            {badge && <span className={`file-type-badge ${badge.className}`}>{badge.text}</span>}
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </aside>
              <main className="modal-content-pane">
                {activeFile ? (
                  activeFile.type.startsWith('image/')
                    ? <img src={activeFile.previewUrl} alt={activeFile.name} />
                    : <iframe src={activeFile.previewUrl} title={activeFile.name} style={{ border: 0 }} />
                ) : (
                  <div className="modal-placeholder">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M4 4h8v2H4zm0 4h12v2H4zm0 4h12v2H4zm14 0-4 4v-8l4 4z"></path></svg>
                    <p>Select a file to preview</p>
                  </div>
                )}
              </main>
            </div>
            <footer className="modal-footer">
               {activeFile && (
                <a href={activeFile.previewUrl} target="_blank" rel="noopener noreferrer">
                  Open "{activeFile.name}" in a new tab
                </a>
               )}
            </footer>
          </div>
        </div>
      )}
    </>
  );
};